import React from 'react';
import { LuEllipsis } from 'react-icons/lu';

import { LifecycleStatus } from '@ndvr/portfolio-api';

import { Breadcrumbs } from '@/components/Breadcrumbs';
import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { AdvisorLoginButton } from 'modules';
import { useHouseholdRoutePaths } from 'utils';
import { useAuthContext } from 'views/auth/Auth';
import { useGlobalAccount } from 'views/household/household-queries';

interface PortfolioPageHeaderProps {
    householdName: string;
    householdGuid: string;
    portfolioName: string;
    portfolioGuid: string;
    portfolioStatus: LifecycleStatus;
}

export const PortfolioPageHeader = ({
    householdName,
    householdGuid,
    portfolioName,
    portfolioGuid,
    portfolioStatus,
}: PortfolioPageHeaderProps) => {
    const { HouseholdListPageRoutePath, PortfolioOverviewTabRoutePath } = useHouseholdRoutePaths();
    const { availableTenants } = useAuthContext();
    const { data: household } = useGlobalAccount(householdGuid);
    const clientBaseName = availableTenants.find((tenant) => tenant.tenantGUID === household?.tenantGUID)?.name;

    return (
        <>
            <PageTopBar />
            <PageTitleRow
                actions={
                    <div className="flex shrink-0 items-center gap-4">
                        <AdvisorLoginButton
                            globalAccountGUID={householdGuid}
                            redirect={`/lab/${portfolioGuid}`}
                            variant="outline2"
                        />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline2" size="sm" className="rounded-full px-2" aria-label="Actions">
                                    <LuEllipsis className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <CopyGuidDropdownMenuItem guid={portfolioGuid} />
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                }
            >
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-3">
                        <PageTitle>{portfolioName}</PageTitle>
                        <StatusBadge status={portfolioStatus} />
                    </div>
                    <Breadcrumbs
                        parts={[
                            ...(clientBaseName ? [{ label: clientBaseName }] : []),
                            { label: 'Households', path: HouseholdListPageRoutePath },
                            { label: householdName, path: PortfolioOverviewTabRoutePath(householdGuid) },
                            { label: portfolioName },
                        ]}
                    />
                </div>
            </PageTitleRow>
        </>
    );
};
